import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  View,
  Text,
  TextArea,
  Spinner,
  Flex,
  IconWarningLine,
  IconEditLine,
} from '@instructure/ui';

/**
 * @component InPlaceEdit
 *
 * @description A component that allows for in-place editing of text content.
 * Displays text that can be clicked to edit, with placeholder support and error handling.
 *
 * @example
 * // Basic usage
 * <InPlaceEdit
 *   text="Edit this text"
 *   placeholder="Add a description"
 *   onChange={handleChange}
 * />
 *
 * // With custom styling
 * <InPlaceEdit
 *   text={description}
 *   placeholder="Add a description"
 *   onChange={handleChange}
 *   textStyle={{ fontWeight: 'bold' }}
 *   placeholderStyle={{ fontStyle: 'italic' }}
 * />
 */
const InPlaceEdit = forwardRef(({

  // Content props
  text = '',
  placeholder = 'Click to edit',

  // Event handlers
  onChange,

  // Styling props
  textStyle = {},
  placeholderStyle = {},

  // Additional props
  ...otherProps
}, ref) => {
  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [currentText, setCurrentText] = useState(text);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isHovering, setIsHovering] = useState(false);

  // References
  const textAreaRef = useRef(null);

  // Handle click on the text/placeholder to start editing
  const handleTextClick = () => {
    setIsEditing(true);
    setError(null);
  };

  // Expose methods to parent components through ref
  useImperativeHandle(ref, () => ({
    startEditing: handleTextClick,
    focus: handleTextClick // Alias for backward compatibility
  }), []);

  // Update currentText when text prop changes
  useEffect(() => {
    setCurrentText(text);
  }, [text]);

  // Focus the text area when editing starts
  useEffect(() => {
    if (isEditing) {
      // Use a slightly longer timeout to ensure the DOM is fully rendered
      setTimeout(() => {
        if (textAreaRef.current) {
          console.log('Focusing text area');
          // Try multiple focus methods to ensure it works across browsers
          try {
            // Standard focus
            textAreaRef.current.focus();

            // Place cursor at the end of the text
            if (typeof textAreaRef.current.setSelectionRange === 'function') {
              const length = currentText ? currentText.length : 0;
              textAreaRef.current.setSelectionRange(length, length);
            }
          } catch (err) {
            console.error('Error focusing text area:', err);
          }
        } else {
          console.warn('Text area ref is not available');
        }
      }, 50); // Slightly longer timeout
    }
  }, [isEditing, currentText]);

  // Handle text changes in the text area
  const handleTextChange = (e) => {
    setCurrentText(e.target.value);
  };

  // Handle saving the edited text
  const handleSave = async () => {
    const oldText = text;
    const newText = currentText;

    // Don't save if nothing changed
    if (oldText === newText) {
      console.log('No changes detected, exiting edit mode without saving');
      setIsEditing(false);
      return;
    }

    // Only proceed with save if we have an onChange handler and content actually changed
    if (onChange) {
      // Set saving state before exiting edit mode
      setIsSaving(true);
      setError(null);
      setIsEditing(false); // Exit edit mode to show the saving indicator

      try {
        // Call the onChange handler and wait for the result
        const result = await onChange({ oldText, newText });

        // Check if the save was successful
        if (!(result && result.success)) {
          // Handle error from the onChange handler
          setError(result?.message || 'An error occurred while saving');
          // If there's an error, go back to edit mode
          setIsEditing(true);
        }
      } catch (err) {
        // Handle any exceptions
        setError(err.message || 'An unexpected error occurred');
        // If there's an error, go back to edit mode
        setIsEditing(true);
      } finally {
        setIsSaving(false);
      }
    } else {
      // If no onChange handler is provided, just exit edit mode
      setIsEditing(false);
    }
  };

  // Handle blur event on the text area
  const handleBlur = () => {
    handleSave();
  };

  // Handle key events in the text area
  const handleKeyDown = (e) => {
    // Save on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }

    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      setCurrentText(text);
      setIsEditing(false);
      setError(null);
    }
  };

  return (
    <View as="div" {...otherProps}>
      {isEditing ? (
        // Edit mode - show text area
        <View as="div">
          <TextArea
            label=""
            screenReaderLabel="Edit text"
            value={currentText}
            placeholder={placeholder}
            onChange={handleTextChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            height="auto"
            minHeight="2.375rem"
            elementRef={(el) => {
              textAreaRef.current = el;
            }}
          />

          {/* Error message */}
          {error && (
            <View as="div" margin="x-small 0 0 0">
              <Flex>
                <Flex.Item>
                  <IconWarningLine color="danger" />
                </Flex.Item>
                <Flex.Item margin="0 0 0 x-small">
                  <Text color="danger" size="small">
                    {error}
                  </Text>
                </Flex.Item>
              </Flex>
            </View>
          )}
        </View>
      ) : (
        // Display mode - show text or placeholder
        <View as="div">
          {/* Show text or placeholder */}
          <View
            as="div"
            cursor="pointer"
            onClick={handleTextClick}
            padding="x-small none"
            borderRadius="small"
            background="transparent"
            hover={{ background: 'brand' }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <Flex>
              <Flex.Item shouldGrow shouldShrink padding="none none none xx-small">
                {currentText ? (
                  <Text
                    variant="contentSmall"
                    color="primary"
                    style={textStyle}
                  >
                    {currentText}
                  </Text>
                ) : (
                  <Text
                    variant="contentSmall"
                    color="secondary"
                    style={placeholderStyle}
                  >
                    {placeholder}
                  </Text>
                )}
              </Flex.Item>
              {isHovering && (
                <Flex.Item padding="none xx-small none none">
                  <IconEditLine size="x-small" color="secondary" />
                </Flex.Item>
              )}
            </Flex>
          </View>

          {/* Saving indicator */}
          {isSaving && (
            <View as="div" margin="x-small 0 0 0">
              <Flex>
                <Flex.Item margin="0 x-small 0 0">
                  <Spinner size="x-small" renderTitle="Saving" />
                </Flex.Item>
                <Flex.Item>
                  <Text size="small">Saving...</Text>
                </Flex.Item>
              </Flex>
            </View>
          )}
        </View>
      )}
    </View>
  );
});

InPlaceEdit.displayName = 'InPlaceEdit';

InPlaceEdit.propTypes = {
  /**
   * The text content to display and edit
   */
  text: PropTypes.string,

  /**
   * Placeholder text to display when there is no content
   */
  placeholder: PropTypes.string,

  /**
   * Callback function called when the text is changed
   * @param {Object} data - Object containing oldText and newText
   * @returns {Object} - Object with success (boolean) and optional message (string)
   */
  onChange: PropTypes.func,

  /**
   * Custom styling for the text content
   */
  textStyle: PropTypes.object,

  /**
   * Custom styling for the placeholder text
   */
  placeholderStyle: PropTypes.object,
};

export default InPlaceEdit;
